import { useEffect, useMemo } from "react";
import { ERolesCode } from "../models/enums/roles/roles-code.enum";
import { ERolesId } from "../models/enums/roles/roles-id.enum";
import { useLoggedUser } from "../store/auth";
import { IUserLoggedEntity } from "../models/interfaces/entity/user-logged/user-logged.entity";
import useCookieSessionManager from "@rf-hooks/useCookieSessionManager";
import ENVIRONMENT_VAR from "@/src-refactor/modules/shared/model/const/env/env.const";
import { debounce } from "lodash";

/**
 * Hook that provides various permission-related functions based on the logged in user's role.
 *
 * @function useUserHasPermission
 * @returns {{
 *  userIs: (...arg: ERolesCode[]) => boolean,
 *  userIsById: (...arg: ERolesId[]) => boolean,
 *  userIsNotById: (...arg: ERolesId[]) => boolean,
 *  userIsNot: (...arg: ERolesCode[]) => boolean,
 *  userIsAdminFromThatAgency: (agency_id: number) => boolean,
 *  userIshimself: (people_id: number) => boolean
 * userIsAgent: () => boolean,
 * isUserTypeAdministrator: () => boolean,
 * userIsBrokerFromThatAgency: (agency_id: number) => boolean
 * }} - An object with permission-related functions.
 */
function useUserHasPermission() {
  const { NODE_ENV } = ENVIRONMENT_VAR;
  const { userLogged, setUserData } = useLoggedUser();
  const { setSessionTokenUser } = useCookieSessionManager();

  const userAgency = useMemo(() => {
    if (userLogged.userData?.agency.id) {
      return [
        {
          label: userLogged.userData.agency.name,
          value: userLogged.userData.agency.id
        }
      ];
    } else {
      return [];
    }
  }, []);

  /**
   * Check if the user has a role that matches any of the given role codes.
   *
   * @function userIs
   * @param {...ERolesCode} arg - The role codes to check against.
   * @returns {boolean} - True if the user has a matching role code, false otherwise.
   * @example
   * const isAdmin = userIs(ERolesCode.ADMIN); // devuelve true si el usuario es un ADMIN
   */
  const userIs = (...arg: ERolesCode[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return arg.includes(userLogged?.userData?.user_type_code?.code as any);
    }
    return false;
  };
  /**
   * Check if the user has a role that matches any of the given role IDs.
   *
   * @function userIsById
   * @param {...ERolesId} arg - The role IDs to check against.
   * @returns {boolean} - True if the user has a matching role ID, false otherwise.
   *  @example
   * const isManager = userIsById(ERolesId.MANAGER); // devuelve true si el usuario es un MANAGER por su ID
   */
  const userIsById = (...arg: ERolesId[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return arg.includes(userLogged.userData?.user_type_code.id);
    }
    return false;
  };

  /**
   * Check if the user does not have a role that matches any of the given role codes.
   *
   * @function userIsNot
   * @param {...ERolesCode} arg - The role codes to check against.
   * @returns {boolean} - True if the user does not have a matching role code, false otherwise.
   * @example
   * const isNotUser = userIsNot(ERolesCode.USER); // devuelve true si el usuario NO es un USER
   */
  const userIsNot = (...arg: ERolesCode[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return !arg.includes(userLogged.userData?.user_type_code.code);
    }
    return false;
  };

  /**
   * Check if the user does not have a role that matches any of the given role IDs.
   *
   * @function userIsNotById
   * @param {...ERolesId} arg - The role IDs to check against.
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   * const isNotSupervisor = userIsNotById(ERolesId.SUPERVISOR); // devuelve true si el usuario NO es un SUPERVISOR por su ID
   */
  const userIsNotById = (...arg: ERolesId[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return !arg.includes(userLogged.userData?.user_type_code.id);
    }
    return false;
  };

  /**
   * Check if the user logged is a agent.
   *
   * @function userIsNotById
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   */
  const userIsAgent = () => {
    if (typeof userLogged?.userData !== "undefined") {
      return [
        ERolesCode.BROKER,
        ERolesCode.SENIOR,
        ERolesCode.JUNIOR,
        ERolesCode.BROKER_MANAGER
      ].includes(userLogged.userData?.user_type_code.code);
    }
    return false;
  };

  /**
   * Check if the user logged is a agent.
   *
   * @function userIsNotById
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   */
  const isUserTypeAdministrator = () => {
    if (typeof userLogged?.userData !== "undefined") {
      return [ERolesCode.SUPER_ADMIN, ERolesCode.ADMIN].includes(
        userLogged.userData?.user_type_code.code
      );
    }
    return false;
  };

  /**
   * Check if the user is an admin for the given agency ID.
   *
   * @function userIsAdminFromThatAgency
   * @param {number} agency_id - The agency ID to check against.
   * @returns {boolean} - True if the user is an admin for the given agency
   * @example
   * const isAdminOfAgency = userIsAdminFromThatAgency(123); // devuelve true si el usuario es un ADMIN de la agencia con ID 123
   */
  const userIsAdminFromThatAgency = (agency_id: number) => {
    if (typeof userLogged?.userData !== "undefined") {
      if (userLogged.userData.user_type_code.id === ERolesId.ADMIN) {
        return userLogged.userData.agency_id === agency_id;
      } else {
        return false;
      }
    }
    return false;
  };

  /**
   * Check if the user is an Broker or broker manager  for the given agency ID.
   *
   * @function userIsBroker or broker manager FromThatAgency
   * @param {number} agency_id - The agency ID to check against.
   * @returns {boolean} - True if the user is an Broker or broker manager  for the given agency
   * @example
   * const isBroker or broker manager OfAgency = userIsBroker or broker manager FromThatAgency(123); // devuelve true si el usuario es un Broker or broker manager  de la agencia con ID 123
   */
  const userIsBrokerFromThatAgency = (agency_id: number) => {
    if (typeof userLogged?.userData !== "undefined") {
      if (
        userLogged.userData.user_type_code.id === ERolesId.BROKER ||
        userLogged.userData.user_type_code.id === ERolesId.BROKER_MANAGER
      ) {
        return userLogged.userData.agency_id === agency_id;
      } else {
        return false;
      }
    }
    return false;
  };

  const userIsHimSelf = (people_id: number) => {
    if (typeof userLogged?.userData !== "undefined") {
      return userLogged.userData?.people_id === people_id;
    }
    return false;
  };

  const USER_ROLE = useMemo(() => {
    return {
      SUPER_ADMIN: userIs(ERolesCode.SUPER_ADMIN),
      ADMIN: userIs(ERolesCode.ADMIN),
      BROKER: userIs(ERolesCode.BROKER),
      BROKER_MANAGER: userIs(ERolesCode.BROKER_MANAGER),
      SENIOR: userIs(ERolesCode.SENIOR),
      JUNIOR: userIs(ERolesCode.JUNIOR),
      CLIENT: userIs(ERolesCode.CLIENT),
      IS_AGENT: userIsAgent(),
      IS_ADMINISTRATOR: isUserTypeAdministrator()
    };
  }, [userLogged?.userData?.user_type_code?.code]);

  const USER_HAS = useMemo(() => {
    return {
      ALTER_ESTATE_TOKEN: Boolean(
        userLogged?.userData?.has_alterestate_token || false
      ),
      INTERNATIONAL_ID: Boolean(userLogged?.userData?.international_id || false)
    };
  }, [JSON.stringify(userLogged)]);

  const getUserState = () => {
    return userLogged;
  };

  const setUserState = (user: IUserLoggedEntity) => {
    setUserData(user);
  };

  const getPreferences = () => {
    const preferences = localStorage.getItem(
      `${PREFERENCE_KEY_USER}-${userLogged.userData?.user_id}-${NODE_ENV}`
    );
    if (preferences) {
      return JSON.parse(preferences) as unknown as IPreferencesUser;
    } else {
      loadPreferences();
    }

    return C_PREFERENCES;
  };

  const updatePreferences = (preferences: IPreferencesUser) => {
    localStorage.setItem(
      `${PREFERENCE_KEY_USER}-${userLogged.userData?.user_id}-${NODE_ENV}`,
      JSON.stringify({ ...C_PREFERENCES, ...preferences })
    );
  };

  const loadPreferences = debounce(async () => {
    const preferences = localStorage.getItem(
      `${PREFERENCE_KEY_USER}-${userLogged.userData?.user_id}-${NODE_ENV}`
    );
    if (!preferences) {
      localStorage.setItem(
        `${PREFERENCE_KEY_USER}-${userLogged.userData?.user_id}-${NODE_ENV}`,
        JSON.stringify(C_PREFERENCES)
      );
    }
  }, 500);

  return {
    userIs,
    userIsById,
    userIsNotById,
    userIsNot,
    userIsAdminFromThatAgency,
    userIsHimSelf,
    userIsAgent,
    isUserTypeAdministrator,
    userIsBrokerFromThatAgency,
    userAgency,
    USER_ROLE,
    USER: userLogged,
    USER_HAS,
    getUserState,
    setUserState,
    setSessionTokenUser,
    getPreferences,
    updatePreferences
  };
}

export default useUserHasPermission;

const PREFERENCE_KEY_USER = "maxcore_preference";

interface IPreferencesUser {
  formPropertyTranslationAlertIsDisable?: boolean;
  weeklyModal?: Record<string, string>;
}
const C_PREFERENCES: IPreferencesUser = {
  formPropertyTranslationAlertIsDisable: false,
  weeklyModal: {}
};
